import { useEffect } from "react";
import { Pressable, Text } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

/**
 * A transient, tap-to-dismiss error notice for the anonymous Pull Sheet browse list (#360).
 *
 * A card's quick-pick "+"/✓ calls add/removePick with no modal open, so a failed tap has no inline
 * surface; this floating pill is that surface. It sits above the sticky `PullSheetBar` like
 * `UndoSnackbar`, styled with a loss border and text over the neutral background so it reads in
 * both themes. It dismisses itself after `timeoutMs` and renders nothing while `message` is null.
 *
 * Check-out and remove errors show inline in `PullSheetReviewModal` instead, since that modal is
 * the visible surface then.
 */
export function PullSheetErrorToast(
  { message, onDismiss, timeoutMs = 4000 }: {
    message: string | null;
    onDismiss: () => void;
    timeoutMs?: number;
  },
) {
  const insets = useSafeAreaInsets();

  useEffect(() => {
    if (!message) return undefined;
    const t = setTimeout(onDismiss, timeoutMs);
    return () => clearTimeout(t);
  }, [message, onDismiss, timeoutMs]);

  if (!message) return null;

  return (
    <Pressable
      onPress={onDismiss}
      accessibilityRole="button"
      accessibilityLabel={`${message} Tap to dismiss.`}
      accessibilityLiveRegion="assertive"
      className="absolute inset-x-0 items-center px-4"
      // Clears the sticky PullSheetBar (about 64px plus its bottom inset padding).
      style={{ bottom: insets.bottom + 84 }}
    >
      <Text className="max-w-full rounded-xl border border-loss bg-bg px-4 py-3 text-center text-sm font-body text-loss">
        {message}
      </Text>
    </Pressable>
  );
}
